import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, throwError, Subject, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  User,
  LoginRequest,
  TokenResponse,
  MessageResponse,
  ActiveSessionsResponse,
  MicrosoftTokenRequest
} from './auth.models';
import { MsalService } from '@azure/msal-angular';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly tokenKey = 'auth_token';
  private readonly refreshTokenKey = 'refresh_token';
  private readonly userKey = 'current_user';

  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  private logoutChannel: BroadcastChannel | null = null;
  private idleTimer: any = null;
  private isClearing = false;
  private readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  constructor(
    private http: HttpClient,
    private router: Router,
    private matDialog: MatDialog,
    @Optional() private msalService: MsalService
  ) {
    // Restaurar usuario de localStorage al iniciar
    const storedUser = localStorage.getItem(this.userKey);
    this.currentUserSubject = new BehaviorSubject<User | null>(
      storedUser ? JSON.parse(storedUser) : null
    );
    this.currentUser$ = this.currentUserSubject.asObservable();

    // Multi-tab logout via BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      this.logoutChannel = new BroadcastChannel('puntomas_logout');
      this.logoutChannel.onmessage = (event) => {
        if (event.data?.action === 'logout') {
          this.clearSession(false); // don't broadcast again
        }
      };
    }

    // Idle timeout - reset on user activity
    if (this.isAuthenticated) {
      this.startIdleTimer();
    }
    this.currentUser$.subscribe(user => {
      if (user) { this.startIdleTimer(); } else { this.stopIdleTimer(); }
    });
  }

  /**
   * Usuario actual
   */
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Verifica si hay un usuario autenticado con token válido
   */
  public get isAuthenticated(): boolean {
    return !!this.currentUserValue && !!this.getToken();
  }

  /**
   * Login con email/usuario y contraseña
   */
  login(email: string, password: string, force = false): Observable<TokenResponse> {
    const request: LoginRequest = { email, password, ...(force && { force }) };

    return this.http
      .post<TokenResponse>(`${this.apiUrl}/api/auth/login`, request)
      .pipe(
        tap((response) => {
          this.storeSession(response);
        }),
        catchError((error) => {
          console.error('Login error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Iniciar flujo de login con Microsoft (Entra ID)
   */
  async loginWithMicrosoftRedirect(): Promise<void> {
    if (!this.msalService) {
      console.error('[Auth] MSAL Service not available');
      return;
    }

    try {
      console.log('[Auth] Starting Microsoft login...');

      // Clear any stuck MSAL state
      this.clearMsalState();

      // Wait for MSAL to be initialized
      await this.msalService.instance.initialize();
      console.log('[Auth] MSAL initialized');

      // Check if there's a pending redirect
      const response = await this.msalService.instance.handleRedirectPromise();
      if (response) {
        console.log('[Auth] Found pending redirect response');
        return;
      }

      // Start login redirect
      console.log('[Auth] Redirecting to Microsoft...');
      await this.msalService.instance.loginRedirect({
        scopes: ['user.read', 'openid', 'profile', 'email'],
        prompt: 'select_account'
      });
    } catch (error: any) {
      console.error('[Auth] Login redirect error:', error);

      if (error.errorCode === 'interaction_in_progress') {
        console.log('[Auth] Clearing interaction state and retrying...');
        this.clearMsalState();
        // Don't reload, just let user try again
      }
      throw error;
    }
  }

  /**
   * Clear stuck MSAL interaction state - both sessionStorage AND localStorage
   * (MSAL is configured to use localStorage for cache, but interaction state
   * also lives in sessionStorage).
   */
  private clearMsalState(): void {
    const removeMsalKeys = (storage: Storage, label: string) => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('login.microsoftonline.com'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => storage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log(`[Auth] Cleared MSAL ${label} keys:`, keysToRemove.length);
      }
    };
    removeMsalKeys(sessionStorage, 'sessionStorage');
    removeMsalKeys(localStorage, 'localStorage');
  }

  /**
   * Intercambiar token de Microsoft por token JWT de nuestro backend
   */
  loginWithMicrosoft(microsoftToken: string): Observable<TokenResponse> {
    const request: MicrosoftTokenRequest = { microsoft_token: microsoftToken };

    return this.http
      .post<TokenResponse>(`${this.apiUrl}/api/auth/entra/callback`, request)
      .pipe(
        tap((response) => {
          this.storeSession(response);
        }),
        catchError((error) => {
          console.error('Microsoft login error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Logout - revoca token en el servidor y limpia sesión local.
   * Siempre completa con éxito desde la perspectiva del componente:
   * si el backend falla (por ejemplo token ya expirado), igualmente
   * limpiamos la sesión local y emitimos un success para que el UI
   * no muestre error.
   */
  logout(): Observable<MessageResponse> {
    // Short-circuit if already logged out (idempotent)
    if (!this.currentUserValue && !this.getToken()) {
      this.clearSession();
      return new Observable(observer => {
        observer.next({ message: 'Sesion ya cerrada' } as MessageResponse);
        observer.complete();
      });
    }

    return this.http
      .post<MessageResponse>(`${this.apiUrl}/api/auth/logout`, {})
      .pipe(
        tap(() => {
          this.clearSession();
        }),
        catchError((error) => {
          // Limpiar sesión local aunque falle el servidor.
          // No propagamos el error: el usuario ya "cerró sesión" localmente.
          console.warn('[Auth] Logout server call failed, cleared local session:', error?.status);
          this.clearSession();
          return of({ message: 'Sesion cerrada localmente' } as MessageResponse);
        })
      );
  }

  /**
   * Logout local sin llamar al servidor (usado por error interceptor)
   */
  logoutLocal(): void {
    this.clearSession(true);
  }

  /**
   * Logout completo que también cierra la sesión en Microsoft.
   * Hace redirect a login.microsoftonline.com/logout y vuelve al /auth/login.
   * Úsalo cuando quieras forzar al usuario a ingresar credenciales de nuevo.
   */
  logoutWithMicrosoft(): void {
    this.clearSession(true);
    if (this.msalService) {
      try {
        this.matDialog.closeAll();
        this.msalService.instance.logoutRedirect({
          postLogoutRedirectUri: `${window.location.origin}/auth/login`
        });
      } catch (err) {
        console.warn('[Auth] MSAL logoutRedirect failed:', err);
      }
    }
  }

  /**
   * Refrescar el access token usando el refresh token
   */
  refreshToken(): Observable<TokenResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearSession();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<TokenResponse>(`${this.apiUrl}/api/auth/refresh`, {
        refresh_token: refreshToken
      })
      .pipe(
        tap((response) => {
          this.storeSession(response);
        }),
        catchError((error) => {
          this.clearSession();
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtener datos del usuario actual desde el servidor
   */
  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/api/auth/me`).pipe(
      tap((user) => {
        localStorage.setItem(this.userKey, JSON.stringify(user));
        this.currentUserSubject.next(user);
      })
    );
  }

  /**
   * Obtener sesiones activas del usuario
   */
  getActiveSessions(): Observable<ActiveSessionsResponse> {
    return this.http.get<ActiveSessionsResponse>(
      `${this.apiUrl}/api/auth/sessions`
    );
  }

  /**
   * Cambiar la contraseña del usuario actual.
   * Requiere la contraseña actual + la nueva.
   */
  changeOwnPassword(currentPassword: string, newPassword: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(
      `${this.apiUrl}/api/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword }
    );
  }

  /**
   * Obtener token de acceso
   */
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Obtener refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Guardar sesión en localStorage
   */
  private storeSession(response: TokenResponse): void {
    localStorage.setItem(this.tokenKey, response.access_token);
    if (response.refresh_token) {
      localStorage.setItem(this.refreshTokenKey, response.refresh_token);
    }
    localStorage.setItem(this.userKey, JSON.stringify(response.user));
    this.currentUserSubject.next(response.user);
  }

  /**
   * Limpiar sesión de localStorage
   */
  private clearSession(broadcast = true): void {
    // Idempotent: don't re-enter if already clearing
    if (this.isClearing) return;
    this.isClearing = true;

    // 1. Clear our app tokens
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);

    // 2. Clear MSAL state (sessionStorage + localStorage)
    this.clearMsalState();

    // 3. Try MSAL clearCache (returns Promise in v3+)
    if (this.msalService) {
      try {
        const result: any = this.msalService.instance.clearCache();
        if (result && typeof result.catch === 'function') {
          result.catch(() => { /* ignore */ });
        }
      } catch (_) { /* ignore */ }
    }

    // 4. Notify other tabs
    if (broadcast && this.logoutChannel) {
      try {
        this.logoutChannel.postMessage({ action: 'logout' });
      } catch (_) { /* ignore */ }
    }

    // 5. Update Angular state
    this.currentUserSubject.next(null);
    this.stopIdleTimer();

    // 6. Navigate to login (do this last so subscribers get the null user first)
    this.router.navigate(['/auth/login'], { replaceUrl: true })
      .finally(() => { this.isClearing = false; });
  }

  /**
   * Idle timeout management
   */
  private startIdleTimer(): void {
    this.stopIdleTimer();
    const resetTimer = () => {
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        if (this.isAuthenticated) {
          console.warn('[Auth] Idle timeout - logging out');
          this.logout().subscribe({ error: () => {} });
        }
      }, this.IDLE_TIMEOUT_MS);
    };
    ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });
    resetTimer();
  }

  private stopIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Reset activity (called externally if needed)
   */
  resetIdleTimer(): void {
    if (this.isAuthenticated) {
      this.startIdleTimer();
    }
  }
}
